import type React from 'react';
import { useRef, useState } from 'react';

import type { VideoStory } from '#src/app/pages/about/impact/pageContext';

/** Minimum horizontal travel (px) for a pointer gesture to count as a swipe. */
const SWIPE_THRESHOLD = 40;
/** How long the click fired at the end of a swipe gesture is ignored for. */
const SWIPE_CLICK_GUARD_MS = 50;

type Result = ModelHookResult<
	{
		activeIndex: number;
		playingIndex: number | null;
		count: number;
	},
	{
		next: () => void;
		prev: () => void;
		goTo: (index: number) => void;
		play: (index: number) => void;
		onPointerDown: (event: Pick<React.PointerEvent, 'clientX'>) => void;
		onPointerUp: (event: Pick<React.PointerEvent, 'clientX'>) => void;
	}
>;

export function useVideoCarousel(stories: readonly VideoStory[]): Result {
	const count = stories.length;
	const [activeIndex, setActiveIndex] = useState(0);
	const [playingIndex, setPlayingIndex] = useState<number | null>(null);
	// pointer coordinates are transient gesture state, never rendered
	const pointerStartXRef = useRef<number | null>(null);
	const swipedRef = useRef(false);

	const changeActiveIndex = (index: number) => {
		if (index === activeIndex) return;
		setActiveIndex(index);
		// unmounting the iframe is what stops playback; the slide goes back to its poster
		setPlayingIndex(null);
	};

	const next = () => changeActiveIndex((activeIndex + 1) % count);
	const prev = () => changeActiveIndex((activeIndex - 1 + count) % count);

	return {
		data: { activeIndex, playingIndex, count },
		actions: {
			next,
			prev,
			// the click that ends a swipe lands on a slide or its play button; the swipe already moved the carousel
			goTo: (index: number) => {
				if (swipedRef.current) return;
				changeActiveIndex(index);
			},
			// activating a dimmed slide's play button both centres and plays it
			play: (index: number) => {
				if (swipedRef.current) return;
				setActiveIndex(index);
				setPlayingIndex(index);
			},
			onPointerDown: ({ clientX }) => {
				pointerStartXRef.current = clientX;
			},
			onPointerUp: ({ clientX }) => {
				const startX = pointerStartXRef.current;
				pointerStartXRef.current = null;
				if (startX === null) return;

				const delta = clientX - startX;
				if (Math.abs(delta) <= SWIPE_THRESHOLD) return;

				if (delta < 0) next();
				else prev();

				swipedRef.current = true;
				window.setTimeout(() => {
					swipedRef.current = false;
				}, SWIPE_CLICK_GUARD_MS);
			},
		},
	};
}
